import React, { useState, useEffect, useMemo } from 'react';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Alert,
  AlertDescription,
} from '@roger/ui-components';
import { Button } from '@roger/ui-components';
import { Input } from '@roger/ui-components';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@roger/ui-components';
import { Badge } from '@roger/ui-components';
import { Switch } from '@roger/ui-components';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@roger/ui-components';
import {
  VirtualizedCombobox,
  type VirtualizedComboboxOption,
} from '@roger/ui-components';
import {
  Loader2,
  RefreshCw,
  Edit,
  AlertTriangle,
  Plus,
  Copy,
  Check,
} from 'lucide-react';
import {
  getAgencyInsurances,
  getAgencyClinics,
  syncClinicsFromClinicInfo,
  createAgencyClinic,
  createAgencyInsurance,
  updateAgencyInsurance,
  deleteAgencyInsurance,
  duplicateClinicInsurances,
  refreshPayorEnrollments,
  type AgencyInsuranceResponse,
  type AgencyClinicResponse,
} from '../services/clinicsApi';
import { updateAgencyClinicAgencyClinicsAgencyClinicIdPut } from '../services/api/sdk.gen';
import { US_STATES } from '../constants/options';
import PhysiciansSection from './Admin/PhysiciansSection';
import { ChartWorkflowSection } from '../components/admin';
import { NpiInput } from '../components/NpiInput';
import { sanitizeNpiInput, validateNpi } from '../utils/npiValidation';
import { validateCcn } from '../utils/ccnValidation';
import { formatPhone, formatStoredPhone } from '../utils/intake';
import { runValidation } from '../components/intake/validators';
import { ClinicSelect } from '../components/clinic/ClinicSelect';
import InsuranceModal from '../components/admin/InsuranceModal';
import type {
  InsuranceForm,
  InsuranceModalTab,
} from '../components/admin/InsuranceModal';
import { FeatureFlag } from '../constants/featureFlags';
import { InsuranceTable } from '../components/admin/InsuranceTable';
import {
  isMedicarePayorType,
  formatDuplicateInsurancePreview,
  previewDuplicateInsurances,
} from '../components/admin/insuranceUtils';
import { getAgencyClinicDisplayName } from '../utils/agencyClinicDisplayName';
import { clinicParticipatesInKinnser } from '../utils/clinicKeyUtils';

type ClinicEditForm = {
  name?: string;
  npi?: string;
  ein?: string;
  address?: string;
  city?: string;
  state?: string | null;
  zip_code?: string;
  phone?: string;
  hha_agency_id?: string;
  cms_branch_id?: string;
  shp_provider_no?: string;
  use_kinnser_wrm?: boolean;
  auto_mrn?: boolean;
  using_rcd?: boolean;
  default_intake_emr_is_roger?: boolean;
  timezone?: string | null;
};

// IANA timezone names, sourced from the browser's ICU tzdata (Intl) rather
// than a hand-maintained list — the backend validates the chosen value
// against Python's zoneinfo/IANA tzdata independently on write.
interface IntlWithSupportedValuesOf {
  supportedValuesOf(input: 'timeZone'): string[];
}
const TIMEZONE_VALUES: string[] =
  typeof Intl !== 'undefined' && 'supportedValuesOf' in Intl
    ? (Intl as unknown as IntlWithSupportedValuesOf).supportedValuesOf(
        'timeZone'
      )
    : [];

// Human-readable label for an IANA timezone, e.g. "America/Los_Angeles" ->
// "America/Los Angeles", "America/Indiana/Indianapolis" ->
// "America/Indiana - Indianapolis".
function formatTimezoneLabel(tz: string): string {
  const [prefix, ...rest] = tz.split('/');
  return `${prefix}/${rest.join(' - ').replace(/_/g, ' ')}`;
}

// All IANA zones are offered (matching backend validation, which accepts any
// valid IANA name) so US territories outside the America/ prefix — e.g.
// Pacific/Honolulu, Pacific/Guam — stay selectable.
const TIMEZONE_OPTIONS: VirtualizedComboboxOption[] = TIMEZONE_VALUES.map(
  tz => ({ value: tz, label: formatTimezoneLabel(tz) })
).sort((a, b) => a.label.localeCompare(b.label));

/**
 * Mapped to Kinnser? cell (EMR-375):
 * - Mapped: checkmark; hover shows Kinnser roster + insurance counts
 * - Not mapped: em dash
 */
function ClinicMappedToKinnser({
  clinic,
  insuranceCount,
}: {
  clinic: AgencyClinicResponse;
  insuranceCount: number;
}) {
  if (!clinicParticipatesInKinnser(clinic)) {
    return <span className='text-muted-foreground'>—</span>;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type='button'
            className='inline-flex items-center justify-center rounded-full text-green-600 hover:text-green-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
            aria-label='Mapped to Kinnser'
          >
            <Check className='h-4 w-4' aria-hidden />
          </button>
        </TooltipTrigger>
        <TooltipContent className='space-y-1'>
          <p className='font-medium'>Kinnser Data:</p>
          <p>Physicians: {clinic.physicians?.length || 0}</p>
          <p>Clinicians: {clinic.clinicians?.length || 0}</p>
          <p>Case Managers: {clinic.case_managers?.length || 0}</p>
          <p>Branches: {clinic.branches?.length || 0}</p>
          <p>Insurances: {insuranceCount}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

const Admin: React.FC = () => {
  const {
    user,
    showSuccess,
    showError,
    hasAnyPermission,
    hasPermission,
    hasFeatureFlag,
    fetchAgencyClinics,
  } = useStore(
    useShallow(state => ({
      user: state.user,
      showSuccess: state.showSuccess,
      showError: state.showError,
      hasAnyPermission: state.hasAnyPermission,
      hasPermission: state.hasPermission,
      hasFeatureFlag: state.hasFeatureFlag,
      fetchAgencyClinics: state.fetchAgencyClinics,
    }))
  );

  const isSuperUser = hasPermission('admin:super-user');

  // Clinic management state
  const [isLoadingClinicInfo, setIsLoadingClinicInfo] = useState(false);
  const [physiciansRefreshToken, setPhysiciansRefreshToken] = useState(0);

  // Clinics list state
  const [clinics, setClinics] = useState<AgencyClinicResponse[]>([]);
  const [isLoadingClinics, setIsLoadingClinics] = useState(false);
  const [isClinicModalOpen, setIsClinicModalOpen] = useState(false);
  const [clinicModalMode, setClinicModalMode] = useState<'create' | 'edit'>(
    'edit'
  );
  const [editingClinicId, setEditingClinicId] = useState<string | null>(null);
  const [editingClinicHasKey, setEditingClinicHasKey] = useState(false);
  const [clinicEditForm, setClinicEditForm] = useState<ClinicEditForm>({});
  const [isSavingClinic, setIsSavingClinic] = useState(false);

  // Insurance list state
  const [insurances, setInsurances] = useState<AgencyInsuranceResponse[]>([]);
  const [refreshingClinicId, setRefreshingClinicId] = useState<string | null>(
    null
  );
  const [isLoadingInsurances, setIsLoadingInsurances] = useState(false);
  const [insuranceSearch, setInsuranceSearch] = useState('');
  // Single-clinic view — no "All Clinics"; default to the first clinic once loaded.
  const [clinicFilter, setClinicFilter] = useState<string>('');

  // Insurance create/edit modal state
  const [isInsuranceModalOpen, setIsInsuranceModalOpen] = useState(false);
  const [insuranceModalMode, setInsuranceModalMode] = useState<
    'create' | 'edit'
  >('create');
  const [editingInsuranceId, setEditingInsuranceId] = useState<string | null>(
    null
  );
  // True when editing a WellSky-synced insurance (clinic_key set): modal is view-only.
  const [editingInsuranceSynced, setEditingInsuranceSynced] = useState(false);
  // Tab the insurance modal opens on: a table-row rates action deep-links
  // straight to the Billing rates tab.
  const [insuranceModalInitialTab, setInsuranceModalInitialTab] =
    useState<InsuranceModalTab>('details');
  const defaultInsuranceForm: InsuranceForm = {
    agency_clinic_id: '',
    name: '',
    edi_payer_id: '',
    edi_clearinghouse: '',
    visit_authorization_required: false,
    noa_required: false,
    payor_type: null,
    billing_frequency: 'MONTHLY',
    pdgm_enabled: false,
    charge_type: 'VISIT',
    do_not_multiply_charges_by_units: false,
    health_plan_id: '',
    provider_code: '',
    provider_number: '',
    address_one: '',
    address_two: '',
    city: '',
    state: '',
    zip_code: '',
  };
  const [insuranceForm, setInsuranceForm] =
    useState<InsuranceForm>(defaultInsuranceForm);
  const [isSavingInsurance, setIsSavingInsurance] = useState(false);

  // Delete confirmation state
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deletingInsuranceId, setDeletingInsuranceId] = useState<string | null>(
    null
  );
  const [isDeletingInsurance, setIsDeletingInsurance] = useState(false);

  // Duplicate-insurances modal state
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [duplicateSourceClinicId, setDuplicateSourceClinicId] = useState('');
  const [duplicateDestClinicId, setDuplicateDestClinicId] = useState('');
  const [isDuplicatingInsurances, setIsDuplicatingInsurances] = useState(false);

  const isKinnserEnabled = user?.agency?.is_kinnser_enabled ?? false;
  const isRogerEmrEnabled = user?.agency?.is_rogeremr_enabled ?? false;
  const showBillingView = hasFeatureFlag(FeatureFlag.ADMIN_TAB_BILLING_VIEW);

  // Colspan for loading/empty rows:
  //   Base: 6 (Name, Clinic Key, NPI, EIN, Address, Actions)
  //   Kinnser: +1 Mapped to Kinnser?
  const clinicTableColSpan = isKinnserEnabled ? 7 : 6;

  const clinicsById = useMemo(() => {
    const map = new Map<string, AgencyClinicResponse>();
    for (const clinic of clinics) {
      map.set(clinic.id, clinic);
    }
    return map;
  }, [clinics]);

  const insuranceCountByClinicId = useMemo(() => {
    const counts = new Map<string, number>();
    for (const insurance of insurances) {
      const clinicId = insurance.agency_clinic_id;
      if (!clinicId) continue;
      counts.set(clinicId, (counts.get(clinicId) ?? 0) + 1);
    }
    return counts;
  }, [insurances]);

  const loadClinics = async () => {
    if (!user?.agency?.id) return;

    setIsLoadingClinics(true);
    try {
      const data = await getAgencyClinics(user.agency.id);
      setClinics(data);
      // Keep the insurance list on a real clinic; pick the first if unset/stale.
      setClinicFilter(prev =>
        prev && data.some(clinic => clinic.id === prev)
          ? prev
          : (data[0]?.id ?? '')
      );
    } catch (error) {
      console.error('Failed to load clinics:', error);
      showError('Failed to load clinic data');
    } finally {
      setIsLoadingClinics(false);
    }
  };

  /** Refresh Admin list and global store so intake / other routes see clinic edits (store caches ~5m). */
  const loadClinicsAndSyncStore = async () => {
    await loadClinics();
    if (user?.agency?.id) {
      await fetchAgencyClinics(user.agency.id, { force: true });
    }
  };

  // Load insurances
  const loadInsurances = async () => {
    if (!user?.agency?.id) return;

    setIsLoadingInsurances(true);
    try {
      const data = await getAgencyInsurances(user.agency.id);
      setInsurances(data);
    } catch (error) {
      console.error('Failed to load insurances:', error);
      showError('Failed to load insurance data');
    } finally {
      setIsLoadingInsurances(false);
    }
  };

  useEffect(() => {
    if (user?.agency?.id) {
      loadClinics();
      loadInsurances();
    }
  }, [user?.agency?.id]);

  const handleRefreshEnrollments = async (agencyClinicId: string) => {
    setRefreshingClinicId(agencyClinicId);
    try {
      const { updatedCount, insurances: updated } =
        await refreshPayorEnrollments(agencyClinicId);
      // Splice the clinic's refreshed rows back into the full list.
      setInsurances(prev => {
        const byId = new Map(updated.map(row => [row.id, row]));
        return prev.map(row => byId.get(row.id) ?? row);
      });
      if (updatedCount === 0) {
        showSuccess(
          'No enrollment updates',
          'Stedi returned no enrollment records for this clinic.'
        );
      } else {
        showSuccess(
          'Enrollment status updated',
          'Fetched the latest enrollment status from Stedi.'
        );
      }
    } catch (error) {
      console.error('Failed to refresh payor enrollments:', error);
      showError(
        'Refresh failed',
        'Unable to fetch enrollment status. Please try again.'
      );
    } finally {
      setRefreshingClinicId(null);
    }
  };

  // Rules of Hooks: permission check must come after all hook calls
  if (!hasAnyPermission(['external-admin:access', 'emr:manage'])) {
    return (
      <div className='container mx-auto px-4 py-6'>
        <Alert className='border-red-200 bg-red-50'>
          <AlertDescription className='text-red-800 flex gap-2 items-center'>
            <AlertTriangle className='h-4 w-4 text-red-600' />
            You don't have permission to access Admin features. Please contact
            your administrator if you need access.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Handle get clinic info
  const handleSyncClinicInfo = async () => {
    if (!user?.agency?.system_name) {
      showError('No agency system name found');
      return;
    }

    setIsLoadingClinicInfo(true);

    try {
      // Sync clinic info to database (this now fetches from RogerDoc API internally)
      const syncResult = await syncClinicsFromClinicInfo();
      showSuccess(
        `Clinic, insurance, and physician information synced successfully. ` +
          `Clinics: ${syncResult.clinics.created_count} created, ${syncResult.clinics.updated_count} updated. ` +
          `Insurances: ${syncResult.insurances.created_count} created, ${syncResult.insurances.updated_count} updated. ` +
          `Physicians: ${syncResult.physicians.created_count} created, ${syncResult.physicians.updated_count} updated.`
      );
      await loadClinicsAndSyncStore(); // Reload clinics after successful sync
      await loadInsurances(); // Reload insurances after successful sync
      setPhysiciansRefreshToken(token => token + 1); // Reload physicians after successful sync
    } catch (error) {
      console.error('Failed to get clinic info:', error);

      // Scraping disabled - show error instead
      showError(
        'Failed to retrieve clinic information. Scraping is currently disabled.'
      );
    } finally {
      setIsLoadingClinicInfo(false);
    }
  };

  // Handle opening create clinic dialog
  const handleCreateClinic = () => {
    setClinicModalMode('create');
    setEditingClinicId(null);
    setEditingClinicHasKey(false);
    setClinicEditForm({
      name: '',
      use_kinnser_wrm: false,
      auto_mrn: false,
      using_rcd: false,
      default_intake_emr_is_roger: false,
    });
    setIsClinicModalOpen(true);
  };

  // Normalize EIN to digits-only (Stedi rejects hyphens/other chars)
  const normalizeEin = (value: string) => value.replace(/\D/g, '');

  // Handle opening edit clinic dialog
  const handleEditClinic = (clinic: AgencyClinicResponse) => {
    setClinicModalMode('edit');
    setEditingClinicId(clinic.id);
    setEditingClinicHasKey(clinicParticipatesInKinnser(clinic));
    setClinicEditForm({
      name: clinic.name || '',
      npi: clinic.npi || '',
      ein: normalizeEin(clinic.ein || ''),
      address: clinic.address || '',
      city: clinic.city || '',
      state: clinic.state || '',
      zip_code: clinic.zip_code || '',
      phone: formatStoredPhone(clinic.phone),
      hha_agency_id: clinic.hha_agency_id || '',
      cms_branch_id: clinic.cms_branch_id || '',
      shp_provider_no: clinic.shp_provider_no || '',
      use_kinnser_wrm: clinic.use_kinnser_wrm ?? false,
      auto_mrn: clinic.auto_mrn ?? false,
      using_rcd: clinic.using_rcd ?? false,
      default_intake_emr_is_roger: clinic.default_intake_emr_is_roger ?? false,
      timezone: clinic.timezone ?? null,
    });
    setIsClinicModalOpen(true);
  };

  // Handle clinic save (create or update)
  const handleSaveClinic = async () => {
    const trimmedClinicName = clinicEditForm.name?.trim() ?? '';

    if (clinicModalMode === 'create') {
      if (!trimmedClinicName) {
        showError('Clinic name is required');
        return;
      }
    } else if (!editingClinicId) {
      return;
    }

    const npiSanitized = sanitizeNpiInput(clinicEditForm.npi || '');
    const npiValidationError = validateNpi(npiSanitized);
    if (npiValidationError) {
      showError(npiValidationError);
      return;
    }

    const ccnValidationError = validateCcn(clinicEditForm.hha_agency_id || '');
    if (ccnValidationError) {
      showError(ccnValidationError);
      return;
    }

    const phoneValidationError = runValidation(
      'validate_phone',
      clinicEditForm.phone
    );
    if (phoneValidationError) {
      showError(phoneValidationError);
      return;
    }

    if (clinicModalMode === 'create') {
      if (!user?.agency?.id) return;

      setIsSavingClinic(true);
      try {
        await createAgencyClinic({
          agency_id: user.agency.id,
          name: trimmedClinicName,
          npi: npiSanitized || undefined,
          ein: normalizeEin(clinicEditForm.ein || '') || undefined,
          address: clinicEditForm.address || undefined,
          city: clinicEditForm.city || undefined,
          state: clinicEditForm.state || undefined,
          zip_code: clinicEditForm.zip_code || undefined,
          phone: clinicEditForm.phone || undefined,
          hha_agency_id: clinicEditForm.hha_agency_id?.trim() || undefined,
          cms_branch_id: clinicEditForm.cms_branch_id?.trim() || undefined,
          shp_provider_no: clinicEditForm.shp_provider_no?.trim() || undefined,
          use_kinnser_wrm: clinicEditForm.use_kinnser_wrm ?? false,
          auto_mrn: clinicEditForm.auto_mrn ?? false,
          using_rcd: clinicEditForm.using_rcd ?? false,
          default_intake_emr_is_roger:
            clinicEditForm.default_intake_emr_is_roger ?? false,
          timezone: clinicEditForm.timezone ?? undefined,
        });
        showSuccess('Clinic created successfully');
        setIsClinicModalOpen(false);
        setClinicEditForm({});
        await loadClinicsAndSyncStore();
      } catch (error) {
        console.error('Failed to create clinic:', error);
        showError('Failed to create clinic');
      } finally {
        setIsSavingClinic(false);
      }
    } else {
      const agencyClinicId = editingClinicId;
      if (agencyClinicId == null) {
        return;
      }
      setIsSavingClinic(true);
      try {
        await updateAgencyClinicAgencyClinicsAgencyClinicIdPut({
          path: { agency_clinic_id: agencyClinicId },
          body: {
            name: clinicEditForm.name || undefined,
            npi: npiSanitized || null,
            ein: normalizeEin(clinicEditForm.ein || '') || undefined,
            address: clinicEditForm.address || undefined,
            city: clinicEditForm.city || undefined,
            state: clinicEditForm.state || undefined,
            zip_code: clinicEditForm.zip_code || undefined,
            phone: clinicEditForm.phone?.trim() || null,
            hha_agency_id: clinicEditForm.hha_agency_id?.trim() || null,
            cms_branch_id: clinicEditForm.cms_branch_id?.trim() || null,
            shp_provider_no: clinicEditForm.shp_provider_no?.trim() || null,
            use_kinnser_wrm: clinicEditForm.use_kinnser_wrm ?? false,
            auto_mrn: clinicEditForm.auto_mrn ?? false,
            using_rcd: clinicEditForm.using_rcd ?? false,
            default_intake_emr_is_roger:
              clinicEditForm.default_intake_emr_is_roger ?? false,
            timezone: clinicEditForm.timezone ?? null,
          },
        });
        showSuccess('Clinic updated successfully');
        setIsClinicModalOpen(false);
        setEditingClinicId(null);
        setClinicEditForm({});
        await loadClinicsAndSyncStore();
      } catch (error) {
        console.error('Failed to update clinic:', error);
        showError('Failed to update clinic');
      } finally {
        setIsSavingClinic(false);
      }
    }
  };

  const handleCancelEdit = () => {
    setIsClinicModalOpen(false);
    setEditingClinicId(null);
    setEditingClinicHasKey(false);
    setClinicEditForm({});
  };

  // Manual insurance create: Roger-only clinics (not Kinnser-participating).
  // Kinnser-participating clinics manage payers via clinic-info sync (EMR-375/379).
  const rogerEmrClinics = clinics.filter(c => !clinicParticipatesInKinnser(c));
  const canAddInsurance = isRogerEmrEnabled && rogerEmrClinics.length > 0;
  // Copy insurance/physicians only into non-Kinnser clinics (Kinnser payers
  // stay sync-owned). Need ≥1 Roger destination and ≥1 other clinic as source.
  const canCopyInsurancesToRogerClinic =
    clinics.length >= 2 && rogerEmrClinics.length >= 1;

  // Insurance modal handlers
  const handleCreateInsurance = () => {
    setInsuranceModalInitialTab('details');
    setInsuranceModalMode('create');
    setEditingInsuranceId(null);
    setEditingInsuranceSynced(false);
    setInsuranceForm(defaultInsuranceForm);
    setIsInsuranceModalOpen(true);
  };

  const handleEditInsurance = (
    insurance: AgencyInsuranceResponse,
    initialTab: InsuranceModalTab = 'details'
  ) => {
    setInsuranceModalInitialTab(initialTab);
    setInsuranceModalMode('edit');
    setEditingInsuranceId(insurance.id);
    setEditingInsuranceSynced(!!insurance.clinic_key);
    setInsuranceForm({
      agency_clinic_id: '',
      name: insurance.name,
      edi_payer_id: insurance.edi_payer_id || '',
      edi_clearinghouse: insurance.edi_clearinghouse || '',
      visit_authorization_required:
        insurance.visit_authorization_required ?? false,
      noa_required: insurance.noa_required ?? false,
      payor_type: insurance.payor_type ?? null,
      billing_frequency: insurance.billing_frequency ?? 'MONTHLY',
      // PDGM only applies to Medicare; normalize non-Medicare rows to false so a
      // backfilled `true` doesn't persist (the toggle is hidden for them).
      pdgm_enabled: isMedicarePayorType(insurance.payor_type ?? null)
        ? (insurance.pdgm_enabled ?? true)
        : false,
      charge_type: insurance.charge_type ?? 'VISIT',
      do_not_multiply_charges_by_units:
        insurance.do_not_multiply_charges_by_units ?? false,
      health_plan_id: insurance.health_plan_id ?? '',
      provider_code: insurance.provider_code ?? '',
      provider_number: insurance.provider_number ?? '',
      address_one: insurance.address_one ?? '',
      address_two: insurance.address_two ?? '',
      city: insurance.city ?? '',
      state: insurance.state ?? '',
      zip_code: insurance.zip_code ?? '',
    });
    setIsInsuranceModalOpen(true);
  };

  const handleSaveInsurance = async () => {
    if (insuranceModalMode === 'create') {
      if (!insuranceForm.agency_clinic_id) {
        showError('Please select a clinic');
        return;
      }
      if (!insuranceForm.name.trim()) {
        showError('Insurance name is required');
        return;
      }
      if (!insuranceForm.payor_type) {
        showError('Please select a payer type');
        return;
      }
      setIsSavingInsurance(true);
      try {
        await createAgencyInsurance({
          agency_clinic_id: insuranceForm.agency_clinic_id,
          name: insuranceForm.name.trim(),
          edi_payer_id: insuranceForm.edi_payer_id.trim() || undefined,
          edi_clearinghouse:
            insuranceForm.edi_clearinghouse.trim() || undefined,
          visit_authorization_required:
            insuranceForm.visit_authorization_required,
          noa_required: insuranceForm.noa_required,
          payor_type: insuranceForm.payor_type!,
          billing_frequency: insuranceForm.billing_frequency,
          pdgm_enabled: insuranceForm.pdgm_enabled,
          charge_type: insuranceForm.charge_type,
          do_not_multiply_charges_by_units:
            insuranceForm.do_not_multiply_charges_by_units,
          health_plan_id: insuranceForm.health_plan_id.trim() || null,
          provider_code: insuranceForm.provider_code.trim() || null,
          provider_number: insuranceForm.provider_number.trim() || null,
          address_one: insuranceForm.address_one.trim() || null,
          address_two: insuranceForm.address_two.trim() || null,
          city: insuranceForm.city.trim() || null,
          state: insuranceForm.state.trim() || null,
          zip_code: insuranceForm.zip_code.trim() || null,
        });
        showSuccess('Insurance created successfully');
        setIsInsuranceModalOpen(false);
        await loadInsurances();
      } catch (error) {
        console.error('Failed to create insurance:', error);
        showError('Failed to create insurance. Please try again.');
      } finally {
        setIsSavingInsurance(false);
      }
    } else {
      if (!editingInsuranceId) return;
      if (!insuranceForm.name.trim()) {
        showError('Insurance name is required');
        return;
      }
      setIsSavingInsurance(true);
      try {
        await updateAgencyInsurance(editingInsuranceId, {
          name: insuranceForm.name.trim(),
          edi_payer_id: insuranceForm.edi_payer_id.trim() || null,
          edi_clearinghouse: insuranceForm.edi_clearinghouse.trim() || null,
          visit_authorization_required:
            insuranceForm.visit_authorization_required,
          noa_required: insuranceForm.noa_required,
          payor_type: insuranceForm.payor_type,
          billing_frequency: insuranceForm.billing_frequency,
          pdgm_enabled: insuranceForm.pdgm_enabled,
          charge_type: insuranceForm.charge_type,
          do_not_multiply_charges_by_units:
            insuranceForm.do_not_multiply_charges_by_units,
          health_plan_id: insuranceForm.health_plan_id.trim() || null,
          provider_code: insuranceForm.provider_code.trim() || null,
          provider_number: insuranceForm.provider_number.trim() || null,
          address_one: insuranceForm.address_one.trim() || null,
          address_two: insuranceForm.address_two.trim() || null,
          city: insuranceForm.city.trim() || null,
          state: insuranceForm.state.trim() || null,
          zip_code: insuranceForm.zip_code.trim() || null,
        });
        showSuccess('Insurance updated successfully');
        setIsInsuranceModalOpen(false);
        await loadInsurances();
      } catch (error) {
        console.error('Failed to update insurance:', error);
        showError('Failed to update insurance. Please try again.');
      } finally {
        setIsSavingInsurance(false);
      }
    }
  };

  const handleCancelInsuranceModal = () => {
    setIsInsuranceModalOpen(false);
    setEditingInsuranceId(null);
    setInsuranceForm(defaultInsuranceForm);
  };

  const handleDeleteInsurance = (insuranceId: string) => {
    setDeletingInsuranceId(insuranceId);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteInsurance = async () => {
    if (!deletingInsuranceId) return;

    setIsDeletingInsurance(true);
    try {
      await deleteAgencyInsurance(deletingInsuranceId);
      showSuccess('Insurance deleted successfully');
      setIsDeleteDialogOpen(false);
      setDeletingInsuranceId(null);
      await loadInsurances();
    } catch (error) {
      console.error('Failed to delete insurance:', error);
      const message =
        error instanceof Error && error.message
          ? error.message
          : 'Failed to delete insurance. Please try again.';
      showError(message);
    } finally {
      setIsDeletingInsurance(false);
    }
  };

  const duplicateInsurancePreview = useMemo(() => {
    if (!duplicateSourceClinicId || !duplicateDestClinicId) {
      return null;
    }
    if (duplicateSourceClinicId === duplicateDestClinicId) {
      return null;
    }
    return previewDuplicateInsurances(
      insurances,
      duplicateSourceClinicId,
      duplicateDestClinicId
    );
  }, [duplicateDestClinicId, duplicateSourceClinicId, insurances]);

  const duplicateInsurancePreviewText = useMemo(() => {
    if (!duplicateSourceClinicId || !duplicateDestClinicId) {
      return null;
    }
    if (duplicateSourceClinicId === duplicateDestClinicId) {
      return 'Source and destination clinics must be different.';
    }
    if (!duplicateInsurancePreview) {
      return null;
    }
    return formatDuplicateInsurancePreview(duplicateInsurancePreview);
  }, [
    duplicateDestClinicId,
    duplicateInsurancePreview,
    duplicateSourceClinicId,
  ]);

  const handleOpenDuplicateInsurances = () => {
    setDuplicateSourceClinicId('');
    setDuplicateDestClinicId('');
    setIsDuplicateModalOpen(true);
  };

  const handleConfirmDuplicateInsurances = async () => {
    if (!duplicateSourceClinicId || !duplicateDestClinicId) {
      showError('Please select both a source and destination clinic');
      return;
    }
    if (duplicateSourceClinicId === duplicateDestClinicId) {
      showError('Source and destination clinics must be different');
      return;
    }
    setIsDuplicatingInsurances(true);
    try {
      const result = await duplicateClinicInsurances(
        duplicateSourceClinicId,
        duplicateDestClinicId
      );
      const copiedRates = result.copied_visit_rate_count ?? 0;
      showSuccess(
        `Copied ${result.created_count} insurance${
          result.created_count === 1 ? '' : 's'
        }` +
          (result.skipped_count > 0
            ? ` (${result.skipped_count} skipped — matching payer ID and name already present)`
            : '') +
          (copiedRates > 0
            ? `, including ${copiedRates} visit rate${
                copiedRates === 1 ? '' : 's'
              }`
            : '')
      );
      setIsDuplicateModalOpen(false);
      await loadInsurances();
    } catch (error) {
      console.error('Failed to copy insurances:', error);
      showError(
        error instanceof Error
          ? error.message
          : 'Failed to copy insurances. Please try again.'
      );
    } finally {
      setIsDuplicatingInsurances(false);
    }
  };

  // Filtered insurances — clinic search uses the live clinic name via FK.
  const filteredInsurances = insurances.filter(insurance => {
    const clinic = insurance.agency_clinic_id
      ? clinicsById.get(insurance.agency_clinic_id)
      : undefined;
    const clinicLabel = clinic
      ? getAgencyClinicDisplayName(clinic, {
          totalClinicCount: clinics.length,
        })
      : insurance.clinic_name;

    const query = insuranceSearch.toLowerCase();
    const matchesSearch =
      insuranceSearch === '' ||
      insurance.name.toLowerCase().includes(query) ||
      insurance.edi_payer_id.toLowerCase().includes(query) ||
      (clinicLabel ?? '').toLowerCase().includes(query);

    const matchesClinic =
      !!clinicFilter && insurance.agency_clinic_id === clinicFilter;

    return matchesSearch && matchesClinic;
  });

  return (
    <div className='space-y-6'>
      <div>
        <h1 className='text-3xl font-bold'>Admin Panel</h1>
        <p className='text-muted-foreground'>
          Administrative functions and system configuration.
        </p>
      </div>

      <div className='grid gap-6'>
        {/* Clinic Management Section */}
        <Card>
          <CardHeader>
            <div className='flex justify-between items-start'>
              <div>
                <CardTitle>Clinic Management</CardTitle>
              </div>
              <div className='flex gap-2'>
                {isRogerEmrEnabled && (
                  <Button onClick={handleCreateClinic} variant='outline'>
                    <Plus className='mr-2 h-4 w-4' />
                    Add Clinic
                  </Button>
                )}
                {isKinnserEnabled && (
                  <Button
                    onClick={handleSyncClinicInfo}
                    disabled={isLoadingClinicInfo}
                    variant='outline'
                  >
                    {isLoadingClinicInfo ? (
                      <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                    ) : (
                      <RefreshCw className='mr-2 h-4 w-4' />
                    )}
                    Sync Clinic Info
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className='space-y-4'>
              <div className='rounded-md border'>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Clinic Name</TableHead>
                      <TableHead>Clinic Key</TableHead>
                      <TableHead>NPI</TableHead>
                      <TableHead>EIN</TableHead>
                      <TableHead>Address</TableHead>
                      {isKinnserEnabled && (
                        <TableHead>Mapped to Kinnser?</TableHead>
                      )}
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoadingClinics ? (
                      <TableRow>
                        <TableCell
                          colSpan={clinicTableColSpan}
                          className='text-center py-8'
                        >
                          <Loader2 className='h-6 w-6 animate-spin mx-auto' />
                          <p className='text-sm text-muted-foreground mt-2'>
                            Loading clinics...
                          </p>
                        </TableCell>
                      </TableRow>
                    ) : clinics.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={clinicTableColSpan}
                          className='text-center py-8 text-muted-foreground'
                        >
                          {user?.agency?.id
                            ? isKinnserEnabled
                              ? 'No clinics found. Try syncing clinic info first.'
                              : 'No clinics found. Click "Add Clinic" to create one.'
                            : 'No agency found. Please ensure you are logged in with an agency.'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      clinics.map(clinic => (
                        <TableRow key={clinic.id}>
                          <TableCell className='font-medium'>
                            {clinic.name ||
                              (clinic.clinic_key && clinics.length === 1 ? (
                                <span className='text-muted-foreground italic'>
                                  Default Clinic
                                </span>
                              ) : (
                                '—'
                              ))}
                          </TableCell>
                          <TableCell className='text-sm text-muted-foreground'>
                            {/* Kinnser: WellSky clinic key. RogerEMR: sequence-assigned key. */}
                            {clinic.clinic_key || '—'}
                          </TableCell>
                          <TableCell>
                            {clinic.npi ? (
                              <Badge variant='secondary'>{clinic.npi}</Badge>
                            ) : (
                              <span className='text-muted-foreground'>—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {clinic.ein ? (
                              <Badge variant='outline'>{clinic.ein}</Badge>
                            ) : (
                              <span className='text-muted-foreground'>—</span>
                            )}
                          </TableCell>
                          <TableCell className='text-sm'>
                            {clinic.address && (
                              <div>
                                {clinic.address}
                                {clinic.city &&
                                  clinic.state &&
                                  clinic.zip_code && (
                                    <div className='text-muted-foreground'>
                                      {clinic.city}, {clinic.state}{' '}
                                      {clinic.zip_code}
                                    </div>
                                  )}
                              </div>
                            )}
                            {!clinic.address && (
                              <span className='text-muted-foreground'>—</span>
                            )}
                          </TableCell>
                          {isKinnserEnabled && (
                            <TableCell>
                              <ClinicMappedToKinnser
                                clinic={clinic}
                                insuranceCount={
                                  insuranceCountByClinicId.get(clinic.id) ?? 0
                                }
                              />
                            </TableCell>
                          )}
                          <TableCell>
                            <div className='flex items-center gap-2'>
                              <Button
                                size='sm'
                                variant='outline'
                                onClick={() => handleEditClinic(clinic)}
                              >
                                <Edit className='h-4 w-4' />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Clinic Create/Edit Modal */}
        <Dialog open={isClinicModalOpen} onOpenChange={setIsClinicModalOpen}>
          <DialogContent className='sm:max-w-[600px] max-h-[85vh] flex flex-col'>
            <DialogHeader>
              <DialogTitle>
                {clinicModalMode === 'create' ? 'Add Clinic' : 'Edit Clinic'}
              </DialogTitle>
              <DialogDescription>
                {clinicModalMode === 'create'
                  ? 'Create a new clinic for your agency.'
                  : 'Update clinic information.'}
              </DialogDescription>
            </DialogHeader>
            <div className='grid gap-4 py-4 overflow-y-auto flex-1 pr-1'>
              <div className='grid gap-2'>
                <label htmlFor='name' className='text-sm font-medium'>
                  Clinic Name{clinicModalMode === 'create' && ' *'}
                </label>
                <Input
                  id='name'
                  value={clinicEditForm.name || ''}
                  onChange={e =>
                    setClinicEditForm(prev => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                  disabled={clinicModalMode === 'edit' && editingClinicHasKey}
                />
                {clinicModalMode === 'edit' && editingClinicHasKey && (
                  <p className='text-xs text-muted-foreground'>
                    Name is synced from an external source and cannot be edited.
                  </p>
                )}
              </div>
              <div className='grid gap-2'>
                <label htmlFor='npi' className='text-sm font-medium'>
                  NPI
                </label>
                <NpiInput
                  id='npi'
                  value={clinicEditForm.npi || ''}
                  onValueChange={npi =>
                    setClinicEditForm(prev => ({
                      ...prev,
                      npi,
                    }))
                  }
                  placeholder='10-digit NPI'
                />
              </div>
              <div className='grid gap-2'>
                <label htmlFor='ein' className='text-sm font-medium'>
                  EIN
                </label>
                <Input
                  id='ein'
                  value={clinicEditForm.ein || ''}
                  onChange={e =>
                    setClinicEditForm(prev => ({
                      ...prev,
                      ein: normalizeEin(e.target.value),
                    }))
                  }
                  placeholder='Digits only (no hyphens)'
                />
              </div>
              <div className='grid gap-2'>
                <label htmlFor='phone' className='text-sm font-medium'>
                  Phone
                </label>
                <Input
                  id='phone'
                  value={clinicEditForm.phone || ''}
                  onChange={e =>
                    setClinicEditForm(prev => ({
                      ...prev,
                      phone: formatPhone(e.target.value),
                    }))
                  }
                  placeholder='555-123-4567'
                />
              </div>
              <div className='grid gap-2'>
                <label htmlFor='hha_agency_id' className='text-sm font-medium'>
                  CCN / PTAN / Medicare Provider Number
                </label>
                <Input
                  id='hha_agency_id'
                  value={clinicEditForm.hha_agency_id || ''}
                  onChange={e =>
                    setClinicEditForm(prev => ({
                      ...prev,
                      // Strip non-alphanumeric and cap at 6 in the handler (not via
                      // DOM maxLength) so a value pasted with spaces/separators is
                      // recovered to the 6-char alphanumeric value OASIS M0010 accepts
                      // instead of being truncated before we can clean it.
                      hha_agency_id: e.target.value
                        .replace(/[^A-Za-z0-9]/g, '')
                        .slice(0, 6),
                    }))
                  }
                  placeholder='e.g. 067890'
                />
                <p className='text-xs text-muted-foreground'>
                  CMS Certification Number (6 letters or digits). Prefills M0010
                  on this clinic's OASIS forms.
                </p>
              </div>
              <div className='grid gap-2'>
                <label htmlFor='cms_branch_id' className='text-sm font-medium'>
                  Branch ID
                </label>
                <Input
                  id='cms_branch_id'
                  value={clinicEditForm.cms_branch_id || ''}
                  onChange={e =>
                    setClinicEditForm(prev => ({
                      ...prev,
                      cms_branch_id: e.target.value,
                    }))
                  }
                  maxLength={10}
                  placeholder='N'
                />
                <p className='text-xs text-muted-foreground'>
                  CMS branch ID. Prefills M0016 on this clinic's OASIS forms.
                  Enter N if this clinic has no branches.
                </p>
              </div>
              <div className='grid gap-2'>
                <label
                  htmlFor='shp_provider_no'
                  className='text-sm font-medium'
                >
                  SHP Provider Number
                </label>
                <Input
                  id='shp_provider_no'
                  value={clinicEditForm.shp_provider_no || ''}
                  onChange={e =>
                    setClinicEditForm(prev => ({
                      ...prev,
                      shp_provider_no: e.target.value,
                    }))
                  }
                  maxLength={16}
                  placeholder='e.g. 48400'
                />
                <p className='text-xs text-muted-foreground'>
                  SHP (Strategic Healthcare Programs) provider number for this
                  clinic. Emitted as the SHPProviderNo column in the CAHPS
                  export.
                </p>
              </div>
              {isSuperUser && (
                <>
                  <div className='flex items-start gap-3'>
                    <Switch
                      id='use_kinnser_wrm'
                      checked={clinicEditForm.use_kinnser_wrm ?? false}
                      onCheckedChange={checked =>
                        setClinicEditForm(prev => ({
                          ...prev,
                          use_kinnser_wrm: checked,
                        }))
                      }
                    />
                    <div className='grid gap-1'>
                      <label
                        htmlFor='use_kinnser_wrm'
                        className='text-sm font-medium leading-none'
                      >
                        Use Kinnser WRM
                      </label>
                      <p className='text-xs text-muted-foreground'>
                        Toggle Kinnser Wellsky Resource Manager for this clinic.
                        This setting is persisted in RogerEMR and is not changed
                        by the Kinnser clinic info sync.
                      </p>
                    </div>
                  </div>
                  <div className='flex items-start gap-3'>
                    <Switch
                      id='auto_mrn'
                      checked={clinicEditForm.auto_mrn ?? false}
                      onCheckedChange={checked =>
                        setClinicEditForm(prev => ({
                          ...prev,
                          auto_mrn: checked,
                        }))
                      }
                    />
                    <div className='grid gap-1'>
                      <label
                        htmlFor='auto_mrn'
                        className='text-sm font-medium leading-none'
                      >
                        Auto-assign MRN on intake (Roger EMR)
                      </label>
                      <p className='text-xs text-muted-foreground'>
                        When enabled, Roger EMR allocates MRNs automatically
                        when an intake is pushed to Roger for this clinic. When
                        pushing to Kinnser, MRN behavior comes from the WellSky
                        catalog in RogerDocs instead.
                      </p>
                    </div>
                  </div>
                  <div className='flex items-start gap-3'>
                    <Switch
                      id='using_rcd'
                      checked={clinicEditForm.using_rcd ?? false}
                      onCheckedChange={checked =>
                        setClinicEditForm(prev => ({
                          ...prev,
                          using_rcd: checked,
                        }))
                      }
                    />
                    <div className='grid gap-1'>
                      <label
                        htmlFor='using_rcd'
                        className='text-sm font-medium leading-none'
                      >
                        Using RCD (Pre-Claim Review)
                      </label>
                      <p className='text-xs text-muted-foreground'>
                        When enabled, episodes for this clinic show Pre-Claim
                        Review fields. This setting is persisted in RogerEMR and
                        is not changed by the Kinnser clinic info sync.
                      </p>
                    </div>
                  </div>
                  <div className='flex items-start gap-3'>
                    <Switch
                      id='default_intake_emr_is_roger'
                      checked={
                        clinicEditForm.default_intake_emr_is_roger ?? false
                      }
                      onCheckedChange={checked =>
                        setClinicEditForm(prev => ({
                          ...prev,
                          default_intake_emr_is_roger: checked,
                        }))
                      }
                    />
                    <div className='grid gap-1'>
                      <label
                        htmlFor='default_intake_emr_is_roger'
                        className='text-sm font-medium leading-none'
                      >
                        Default Intake EMR to RogerEMR
                      </label>
                      <p className='text-xs text-muted-foreground'>
                        When enabled, the Intake Extraction step defaults to
                        RogerEMR for this clinic (instead of the external
                        Kinnser EMR) when the agency has both EMRs. Users can
                        still switch. This setting is persisted in RogerEMR and
                        is not changed by the Kinnser clinic info sync.
                      </p>
                    </div>
                  </div>
                </>
              )}
              <div className='grid gap-2'>
                <label htmlFor='address' className='text-sm font-medium'>
                  Address
                </label>
                <Input
                  id='address'
                  value={clinicEditForm.address || ''}
                  onChange={e =>
                    setClinicEditForm(prev => ({
                      ...prev,
                      address: e.target.value,
                    }))
                  }
                />
              </div>
              <div className='grid grid-cols-2 gap-2'>
                <div className='grid gap-2'>
                  <label htmlFor='city' className='text-sm font-medium'>
                    City
                  </label>
                  <Input
                    id='city'
                    value={clinicEditForm.city || ''}
                    onChange={e =>
                      setClinicEditForm(prev => ({
                        ...prev,
                        city: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className='grid gap-2'>
                  <label htmlFor='state' className='text-sm font-medium'>
                    State
                  </label>
                  <Select
                    value={clinicEditForm.state || 'none'}
                    onValueChange={value =>
                      setClinicEditForm(prev => ({
                        ...prev,
                        state: value === 'none' ? null : value,
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='none'>None</SelectItem>
                      {US_STATES.map(state => (
                        <SelectItem key={state.value} value={state.value}>
                          {state.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className='grid gap-2'>
                <label htmlFor='zip_code' className='text-sm font-medium'>
                  ZIP Code
                </label>
                <Input
                  id='zip_code'
                  value={clinicEditForm.zip_code || ''}
                  onChange={e =>
                    setClinicEditForm(prev => ({
                      ...prev,
                      zip_code: e.target.value,
                    }))
                  }
                />
              </div>
              <div className='grid gap-2'>
                <label htmlFor='timezone' className='text-sm font-medium'>
                  Timezone
                </label>
                <VirtualizedCombobox
                  id='timezone'
                  options={TIMEZONE_OPTIONS}
                  value={clinicEditForm.timezone || ''}
                  onValueChange={value =>
                    setClinicEditForm(prev => ({
                      ...prev,
                      timezone: value || null,
                    }))
                  }
                  placeholder='Use agency default'
                  searchPlaceholder='Search timezones...'
                  height='300px'
                />
                <p className='text-xs text-muted-foreground'>
                  When unset, this clinic uses the agency's timezone.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant='outline' onClick={handleCancelEdit}>
                Cancel
              </Button>
              <Button
                onClick={handleSaveClinic}
                disabled={
                  isSavingClinic ||
                  (clinicModalMode === 'create' && !clinicEditForm.name?.trim())
                }
              >
                {isSavingClinic && (
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                )}
                {clinicModalMode === 'create'
                  ? 'Create Clinic'
                  : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Insurance Providers Section */}
        <Card>
          <CardHeader>
            <div className='flex justify-between items-start'>
              <div>
                <CardTitle>Insurance Providers</CardTitle>
                <CardDescription>
                  {isRogerEmrEnabled
                    ? 'Manage insurance providers configured for your agency'
                    : 'View insurance providers configured for your agency'}
                </CardDescription>
              </div>
              {isRogerEmrEnabled && (
                <div className='flex gap-2'>
                  {isSuperUser && canCopyInsurancesToRogerClinic && (
                    <Button
                      onClick={handleOpenDuplicateInsurances}
                      variant='outline'
                    >
                      <Copy className='mr-2 h-4 w-4' />
                      Copy to Clinic...
                    </Button>
                  )}
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>
                          <Button
                            onClick={handleCreateInsurance}
                            variant='outline'
                            disabled={!canAddInsurance}
                          >
                            <Plus className='mr-2 h-4 w-4' />
                            Add Insurance
                          </Button>
                        </span>
                      </TooltipTrigger>
                      {!canAddInsurance && (
                        <TooltipContent>
                          <p>
                            You need to create a clinic first before adding
                            insurance
                          </p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className='space-y-4'>
            <div className='flex flex-col lg:flex-row gap-4'>
              <div className='flex-1'>
                <Input
                  placeholder='Search insurances...'
                  value={insuranceSearch}
                  onChange={e => setInsuranceSearch(e.target.value)}
                  className='w-full'
                />
              </div>
              <div className='w-full lg:w-64'>
                <ClinicSelect
                  clinics={clinics}
                  value={clinicFilter}
                  onChange={e => setClinicFilter(e.target.value)}
                  placeholder='Select clinic'
                />
              </div>
              <Button
                onClick={loadInsurances}
                disabled={isLoadingInsurances}
                variant='outline'
              >
                {isLoadingInsurances ? (
                  <Loader2 className='h-4 w-4 animate-spin' />
                ) : (
                  <RefreshCw className='h-4 w-4' />
                )}
              </Button>
            </div>

            {isLoadingInsurances ? (
              <div className='rounded-md border py-8 text-center'>
                <Loader2 className='h-6 w-6 animate-spin mx-auto' />
                <p className='text-sm text-muted-foreground mt-2'>
                  Loading insurances...
                </p>
              </div>
            ) : insurances.length === 0 ? (
              <div className='rounded-md border py-8 text-center text-sm text-muted-foreground'>
                {isRogerEmrEnabled
                  ? 'No insurance providers found. Click "Add Insurance" to create one.'
                  : 'No insurance providers found. Try updating from clinic info first.'}
              </div>
            ) : (
              <InsuranceTable
                insurances={filteredInsurances}
                clinics={clinics}
                selectedClinicId={clinicFilter || null}
                isRogerEmrEnabled={isRogerEmrEnabled}
                onEdit={handleEditInsurance}
                onDelete={handleDeleteInsurance}
                showWellSkyBadge={showBillingView}
                showEnrollmentColumns={showBillingView}
                onRefreshEnrollments={
                  isRogerEmrEnabled ? handleRefreshEnrollments : undefined
                }
                refreshingClinicId={refreshingClinicId}
                onEditRates={
                  showBillingView
                    ? insurance => handleEditInsurance(insurance, 'rates')
                    : undefined
                }
              />
            )}
          </CardContent>
        </Card>

        {/* Insurance Create/Edit Modal */}
        {isRogerEmrEnabled && (
          <InsuranceModal
            open={isInsuranceModalOpen}
            mode={insuranceModalMode}
            form={insuranceForm}
            onChange={updates =>
              setInsuranceForm(prev => ({ ...prev, ...updates }))
            }
            onSave={handleSaveInsurance}
            onCancel={handleCancelInsuranceModal}
            clinics={rogerEmrClinics}
            saving={isSavingInsurance}
            wellSkySynced={editingInsuranceSynced}
            showBillingConfig={showBillingView}
            insuranceId={editingInsuranceId}
            initialTab={insuranceModalInitialTab}
          />
        )}

        {/* Duplicate Insurances Modal */}
        {isRogerEmrEnabled && isSuperUser && (
          <Dialog
            open={isDuplicateModalOpen}
            onOpenChange={setIsDuplicateModalOpen}
          >
            <DialogContent className='sm:max-w-[480px]'>
              <DialogHeader>
                <DialogTitle>Copy Insurances to Clinic</DialogTitle>
                <DialogDescription>
                  Copy every insurance from one clinic onto a Roger-only clinic
                  (not Kinnser-linked).
                </DialogDescription>
              </DialogHeader>
              <div className='flex items-start gap-2 rounded-md border border-border bg-muted/50 p-3 text-xs text-muted-foreground'>
                <AlertTriangle className='h-4 w-4 shrink-0 mt-0.5' />
                <span>
                  Creates Roger-managed insurance copies, not WellSky-synced
                  rows. Also copies visit rates onto new insurances, and onto
                  existing matches that have no rates yet. Skips creating
                  insurances that already exist on the destination with the same
                  payer ID and name.
                </span>
              </div>
              <div className='space-y-4 py-2'>
                <div className='space-y-1.5'>
                  <label className='text-sm font-medium'>From clinic</label>
                  <ClinicSelect
                    clinics={clinics}
                    value={duplicateSourceClinicId}
                    onChange={e => setDuplicateSourceClinicId(e.target.value)}
                    placeholder='Select source clinic'
                  />
                </div>
                <div className='space-y-1.5'>
                  <label className='text-sm font-medium'>To clinic</label>
                  <ClinicSelect
                    clinics={rogerEmrClinics.filter(
                      c => c.id !== duplicateSourceClinicId
                    )}
                    value={duplicateDestClinicId}
                    onChange={e => setDuplicateDestClinicId(e.target.value)}
                    placeholder='Select destination clinic'
                  />
                </div>
                {duplicateInsurancePreviewText && (
                  <p className='text-sm text-muted-foreground'>
                    {duplicateInsurancePreviewText}
                  </p>
                )}
              </div>
              <DialogFooter>
                <Button
                  variant='outline'
                  onClick={() => setIsDuplicateModalOpen(false)}
                  disabled={isDuplicatingInsurances}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmDuplicateInsurances}
                  disabled={
                    isDuplicatingInsurances ||
                    !duplicateSourceClinicId ||
                    !duplicateDestClinicId ||
                    duplicateInsurancePreview?.createdCount === 0
                  }
                >
                  {isDuplicatingInsurances && (
                    <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  )}
                  Copy Insurances
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* Insurance Delete Confirmation Dialog */}
        {isRogerEmrEnabled && (
          <Dialog
            open={isDeleteDialogOpen}
            onOpenChange={setIsDeleteDialogOpen}
          >
            <DialogContent className='sm:max-w-[400px]'>
              <DialogHeader>
                <DialogTitle>Delete Insurance</DialogTitle>
                <DialogDescription>
                  Are you sure you want to delete this insurance provider? This
                  action cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  variant='outline'
                  onClick={() => {
                    setIsDeleteDialogOpen(false);
                    setDeletingInsuranceId(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant='destructive'
                  onClick={confirmDeleteInsurance}
                  disabled={isDeletingInsurance}
                >
                  {isDeletingInsurance && (
                    <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  )}
                  Delete
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* Chart Workflow Section (ROG-3268). Gated on the same permissions the
            agency-config PUT enforces, and only where the EMR flows exist. */}
        {isRogerEmrEnabled &&
          (hasPermission('external-admin:access') ||
            hasPermission('emr:manage')) && <ChartWorkflowSection />}

        {hasPermission('external-admin:physicians-section') && (
          <PhysiciansSection
            clinics={clinics}
            isSuperUser={isSuperUser}
            isRogerEmrEnabled={isRogerEmrEnabled}
            refreshToken={physiciansRefreshToken}
          />
        )}
      </div>
    </div>
  );
};

export default Admin;
